import { useReservationBannerStatus } from '@/hooks/useReservationBannerStatus';
import {
  BannerStatus,
  getAccessCode,
  getReservationPropertyName,
} from '@/libs';
import { logger } from '@/libs/logger';
import AppConfig from '@config';
import useLocalization from '@localizations';
import { useNavigation } from '@react-navigation/native';
import {
  Company,
  Reservation,
  dispatchUnlockDoor,
  useAppContext,
  useAppStore,
} from '@store';
import { YourKeyIconBlack } from '@svgs';
import { ActionSheet } from '@/screens/components/ActionSheet';
import { lighten } from 'polished';
import React, { useCallback, useState } from 'react';
import { Linking, StyleSheet, View } from 'react-native';
import { Button, FAB, Text, useTheme } from 'react-native-paper';
import { SwipeLockButton } from './SwipeLockButton';

export const mobileKeyDisabled = (
  reservation: Reservation | undefined,
  bannerStatus = BannerStatus.NONE,
) => {
  return (
    bannerStatus !== BannerStatus.NONE && reservation?.mobile_key_button_enabled
  );
};

export const accessCodeDisabled = (
  company: Company | undefined,
  reservation: Reservation | undefined,
) => {
  return !company?.accessCodeControlEnabled && !reservation?.smw;
};

const renderIcon = (
  reservation: Reservation | undefined,
  bannerStatus: BannerStatus,
) => {
  return (
    <YourKeyIconBlack
      width={AppConfig.Styles.getAdjustedSize(40)}
      opacity={mobileKeyDisabled(reservation, bannerStatus) ? 0.3 : 1}
    />
  );
};

interface IAccessCodeActionSheetProps {
  onDismiss?: () => void;
  isOpen?: boolean;
}

export const AccessCodeActionSheet: React.FC<IAccessCodeActionSheetProps> = ({
  onDismiss,
  isOpen,
}) => {
  const paperTheme = useTheme();
  const { t } = useLocalization();
  const navigation = useNavigation();
  const [loadingLock, setLoadingLock] = useState(false);
  const { company, reservation } = useAppStore();
  const bannerStatus = useReservationBannerStatus(company, reservation);
  const accessCode = getAccessCode(company, reservation, bannerStatus);
  const isMobileKeyDisabled = mobileKeyDisabled(reservation, bannerStatus);
  const isAccessCodeDisabled = accessCodeDisabled(company, reservation);
  const { locale } = useAppContext();

  /**
   * Swipe To Unlock - does API call then redirect to a popup modal page for results
   */
  const onSwipeToUnlock = useCallback(async () => {
    setLoadingLock(true);
    let success = true;
    try {
      if (company) {
        await dispatchUnlockDoor(company.id, company.reservation as string);
      }
    } catch (error) {
      logger.error(error);
      success = false;
    }

    navigation.navigate('Confirmation', {
      contentTitle: t(
        success ? 'unlock_success_title' : 'unlock_unsuccessful_title',
      ),
      contentDescription: t(
        success ? 'unlock_success_subtitle' : 'unlock_unsuccessful_subtitle',
      ),
    });

    setLoadingLock(false);
    onDismiss?.();
  }, [navigation, onDismiss, t, company]);

  const lighterPrimary = lighten(0.1, paperTheme.colors.primary);
  const lastName = reservation?.guestName.split(/[, ]+/).pop() || '';
  const urlObj = `${company?.id}_${reservation?.id}/${lastName}/${locale}`;

  const renderAccessCode = () => {
    if (!company?.accessCodeControlEnabled) {
      return null;
    }

    return (
      <>
        <Text
          style={[
            AppConfig.Styles.subtitle1,
            {
              color: 'white',
              marginBottom: AppConfig.Styles.getAdjustedSize(16),
              marginTop: AppConfig.Styles.getAdjustedSize(16),
            },
          ]}>
          {t('unit_access_code_title')}
        </Text>
        <Text
          style={[
            AppConfig.Styles.headline5,
            {
              color: 'white',
              marginBottom: AppConfig.Styles.getAdjustedSize(24),
            },
          ]}>
          {accessCode}
        </Text>
      </>
    );
  };

  return (
    <>
      <ActionSheet
        isOpen={!!isOpen}
        onClose={onDismiss}
        hideDragIndicator
        backgroundColor={paperTheme.colors.primary}>
        <View
          style={[
            styles.sheetContent,
            { backgroundColor: paperTheme.colors.primary },
          ]}>
          <Text
            style={[
              AppConfig.Styles.subtitle1,
              {
                color: 'white',
                marginBottom: AppConfig.Styles.getAdjustedSize(12),
                marginTop: AppConfig.Styles.getAdjustedSize(25),
              },
            ]}>
            {t('unit_name')}
          </Text>
          <Text
            style={[
              AppConfig.Styles.headline5,
              {
                color: 'white',
                marginBottom: AppConfig.Styles.getAdjustedSize(16),
              },
            ]}>
            {getReservationPropertyName(reservation)}
          </Text>

          {renderAccessCode()}

          <Text
            style={[
              AppConfig.Styles.subtitle2,
              {
                color: 'white',
                marginBottom: AppConfig.Styles.getAdjustedSize(36),
                textAlign: 'center',
              },
            ]}>
            {company?.lockControlEnabled && !isAccessCodeDisabled
              ? t('unit_access_code_subtitle_yourkey')
              : isMobileKeyDisabled && isAccessCodeDisabled
              ? t('unit_access_code_subtitle_yourkey_disabled_wo_access_code')
              : !isMobileKeyDisabled && isAccessCodeDisabled
              ? t('unit_access_code_subtitle_yourkey_wo_access_code')
              : isMobileKeyDisabled
              ? t('unit_access_code_subtitle_yourkey_disabled')
              : t('unit_access_code_subtitle_wo_yourkey')}
          </Text>
          {company?.lockControlEnabled && (
            <SwipeLockButton
              testID="access-code-sheet-swipe-lock-button"
              onEndReached={onSwipeToUnlock}
              loading={loadingLock}
              disabled={
                reservation?.incident.paymentEnabled &&
                !reservation?.incident.paymentConfirmed
              }
            />
          )}
          {reservation?.mobile_key_button_enabled && (
            <>
              <Button
                disabled={isMobileKeyDisabled}
                style={styles.downloadBtn}
                contentStyle={{ minHeight: 70 }}
                labelStyle={[
                  styles.downloadBtnLabel,
                  isMobileKeyDisabled && { opacity: 0.3 },
                ]}
                icon={() => renderIcon(reservation, bannerStatus)}
                onPress={() => {
                  Linking.openURL(
                    `https://3bk5w.app.link/?$canonical_url=http://www.operto.com/${urlObj}&$ios_nativelink=true`,
                  );
                }}>
                {t('launch_yourkey')}
              </Button>
            </>
          )}
          <FAB
            testID="action-sheet-close"
            icon="close"
            onPress={onDismiss}
            color="white"
            style={{
              backgroundColor: lighterPrimary,
              height: 70,
              width: 70,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'center',
              borderRadius: 35,
              marginBottom: AppConfig.Styles.getAdjustedSize(20),
            }}
          />
        </View>
      </ActionSheet>
    </>
  );
};

const styles = StyleSheet.create({
  sheetContent: {
    width: '100%',
    minWidth: AppConfig.Styles.getAdjustedSize(300),
    maxWidth: AppConfig.Styles.responsiveMaxWidth,
    padding: AppConfig.Styles.getAdjustedSize(15),
    alignItems: 'center',
    alignSelf: 'center',
    paddingLeft: AppConfig.Styles.getAdjustedSize(32),
    paddingRight: AppConfig.Styles.getAdjustedSize(32),
  },
  downloadBtn: {
    width: '100%',
    borderRadius: 40,
    backgroundColor: 'white',
    marginBottom: AppConfig.Styles.getAdjustedSize(48),
  },
  downloadBtnLabel: {
    color: 'black',
    fontSize: 16,
    fontWeight: '600',
    textTransform: 'none',
  },
});
